import { gql } from "@apollo/client";
import { ACTION_ITEM_FRAGMENT } from "src/operations/fragments/actionItem";
import {
  CUSTOM_QUESTION_ANSWER_BANK_RELATIONSHIPS_FRAGMENT,
  FORM_VERIFICATION_RESULT_FRAGMENT,
} from "src/operations/fragments/form";
import {
  FORM_QUESTION_WITHOUT_BRANCHING_FRAGMENT,
  FORM_TEMPLATE_FRAGMENT,
  QUESTION_OPTION_FRAGMENT,
} from "src/operations/fragments/formTemplate";
import {
  PERSON_FRAGMENT,
  SIBLING_FRAGMENT,
} from "src/operations/fragments/person";
import { SCHOOL_WITH_GRADES_FRAGMENT } from "src/operations/fragments/school";

export const GET_PREVIOUS_FORM_TEMPLATE = gql`
  query GetPreviousFormTemplate($form_template_id: uuid!) {
    # Note: This view automatically filters out deleted forms / form templates.
    assigned_form_next_form(
      where: {
        has_started: { _eq: false }
        assigned_form_template_id: { _eq: $form_template_id }
      }
      distinct_on: [previous_form_template_id]
    ) {
      previous_form_template {
        id
        name
      }
    }
  }
`;

export const GET_METADATA_BY_FORM_TEMPLATE = gql`
  ${FORM_TEMPLATE_FRAGMENT}
  query GetMetadataByFormTemplate($form_template_id: uuid!) {
    form_template_by_pk(id: $form_template_id) {
      ...FormTemplateFragment
    }
  }
`;

export const GET_PREVIOUS_FORM_BY_FORM_ID = gql`
  ${PERSON_FRAGMENT}
  query GetPreviousFormByFormId($form_id: uuid!) {
    form_by_pk(id: $form_id) {
      id
      form_template {
        id
        enrollment_period_id
      }

      person {
        ...PersonFragment
      }

      form_tags {
        enrollment_period_tag {
          name
        }
      }

      next_forms(where: { deleted_at: { _is_null: true } }) {
        id
        form_template_id
        form_template {
          name
        }
      }

      previous_form {
        id
        form_template_id
        form_template {
          name
        }
        form_school_tags {
          school_id
          enrollment_period_tag {
            id
            name
          }
        }
        form_school_ranks {
          id
          school_id
        }
      }

      previous_offer {
        school {
          id
          name
        }
      }

      previous_waitlist {
        school {
          id
          name
        }
      }

      form_related_grades {
        grade_config {
          id
          label
        }
      }
    }
  }
`;

// This query is shared between parents and admin, but they have different permissions.
// TODO: split this (and other shared queries)
export const GET_FORM_VIEW_BY_ID = gql`
  ${FORM_VERIFICATION_RESULT_FRAGMENT}
  ${CUSTOM_QUESTION_ANSWER_BANK_RELATIONSHIPS_FRAGMENT}
  ${PERSON_FRAGMENT}
  ${SIBLING_FRAGMENT}

  query GetFormViewById(
    $form_id: uuid!
    $organization_id: uuid!
    $is_admin: Boolean!
  ) {
    form_by_pk(id: $form_id) {
      id
      status
      status_updated_at
      submitted_at
      is_hidden_from_parent

      form_attending_school {
        school {
          id
          name
        }
      }

      form_verification_results {
        ...FormVerificationResultFragment
      }

      custom_question_answer_bank_relationships @include(if: $is_admin) {
        ...CustomQuestionAnswerBankRelationshipsFragment
      }

      form_disclaimers(order_by: { signed_at: desc }) {
        id
        signature
        signed_at
      }

      person {
        ...PersonFragment
        # TODO: Consider making the below a "family relationships" fragment. This is reused in GET_STUDENT_BY_ID_ADMIN_PORTAL.
        # Currently we can't make this a fragment because we are filtering by organization_id and fragments can't have variables.
        # In the normal flow of things, we shouldn't have cross-organization relationships, so we may be able to remove this filter.
        first_relationship(
          where: {
            active: { _eq: true }
            second: {
              _and: {
                organization_id: { _eq: $organization_id }
                person_type: { _eq: guardian }
              }
            }
          }
        ) {
          second {
            ...PersonFragment
            person_type

            first_relationship(
              where: {
                active: { _eq: true }
                second: {
                  _and: {
                    organization_id: { _eq: $organization_id }
                    person_type: { _eq: applicant }
                  }
                }
              }
            ) {
              second {
                ...SiblingFragment
              }
            }

            second_relationship(
              where: {
                active: { _eq: true }
                first: {
                  _and: {
                    organization_id: { _eq: $organization_id }
                    person_type: { _eq: applicant }
                  }
                }
              }
            ) {
              first {
                ...SiblingFragment
              }
            }
          }
        }
        second_relationship(
          where: {
            active: { _eq: true }
            first: {
              _and: {
                organization_id: { _eq: $organization_id }
                person_type: { _eq: guardian }
              }
            }
          }
        ) {
          first {
            ...PersonFragment
            person_type

            first_relationship(
              where: {
                active: { _eq: true }
                second: {
                  _and: {
                    organization_id: { _eq: $organization_id }
                    person_type: { _eq: applicant }
                  }
                }
              }
            ) {
              second {
                ...SiblingFragment
              }
            }

            second_relationship(
              where: {
                active: { _eq: true }
                first: {
                  _and: {
                    organization_id: { _eq: $organization_id }
                    person_type: { _eq: applicant }
                  }
                }
              }
            ) {
              first {
                ...SiblingFragment
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_CUSTOM_QUESTION_TYPES_BY_ORG = gql`
  ${FORM_QUESTION_WITHOUT_BRANCHING_FRAGMENT}
  ${QUESTION_OPTION_FRAGMENT}
  query GetCustomQuestionTypesByOrg($organization_id: uuid!) {
    custom_question_type(
      where: { organization_id: { _eq: $organization_id } }
    ) {
      id
      name
      custom_question_type_fields(order_by: { question: { order: asc } }) {
        question_id
        question {
          ...FormQuestionWithoutBranchingFragment
        }
      }
    }
  }
`;

export const GET_SCHOOLS_RANK_VIEW = gql`
  ${SCHOOL_WITH_GRADES_FRAGMENT}
  ${FORM_VERIFICATION_RESULT_FRAGMENT}

  query GetSchoolsRankView(
    $form_id: uuid!
    $enrollment_period_id: uuid!
    $skip_rank: Boolean!
    $filter: form_school_rank_bool_exp!
  ) {
    form_school_rank(
      where: { _and: [{ form_id: { _eq: $form_id } }, $filter] }
    ) {
      id
      is_override
      override_reason
      rank @skip(if: $skip_rank)
      sub_status
      school {
        ...SchoolWithGradesFragment
      }
      form {
        id
        status
        form_verification_results {
          ...FormVerificationResultFragment
        }
      }
      form_verifications_flattened {
        verifications
      }
      form_school_grades {
        grade {
          program {
            label
          }
        }
        bin_id
      }
      offers(where: { deleted_at: { _is_null: true } }) {
        id
        status
        expires_at
        bin_id
      }
      waitlists(where: { deleted_at: { _is_null: true } }) {
        id
        status
        waitlist_position {
          position
        }
      }
      tags(order_by: { enrollment_period_tag: { name: asc } }) {
        id
        enrollment_period_tag {
          id
          name
          tag_group_id
          tag_group {
            id
            name
          }
        }
      }
    }
    tag_group(
      where: { enrollment_period_id: { _eq: $enrollment_period_id } }
      order_by: { name: asc }
    ) {
      id
      name
      is_external
      max_tags
    }
  }
`;

export const GET_TAG_GROUPS_BY_ENROLLMENT_PERIOD = gql`
  query GetTagGroupsByEnrollmentPeriod(
    $enrollment_period_id: uuid!
    $is_external: Boolean_comparison_exp = {}
  ) {
    tag_group(
      where: {
        enrollment_period_id: { _eq: $enrollment_period_id }
        is_external: $is_external
      }
      order_by: { name: asc }
    ) {
      id
      name
      is_external
      max_tags
      is_default
    }
  }
`;

export const GET_SCHOOLS_FOR_FILTERS = gql`
  query GetSchoolsForFilters($form_template_id: uuid!) {
    school(
      where: {
        _or: [
          {
            form_school_ranks: {
              form: {
                form_template_id: { _eq: $form_template_id }
                deleted_at: { _is_null: true }
              }
            }
          }
          {
            offers: {
              previous_forms: {
                form_template_id: { _eq: $form_template_id }
                deleted_at: { _is_null: true }
              }
            }
          }
          {
            waitlists: {
              previous_forms: {
                form_template_id: { _eq: $form_template_id }
                deleted_at: { _is_null: true }
              }
            }
          }
        ]
      }
    ) {
      id
      name
    }
    attending_school: school(
      where: {
        form_attending_schools: {
          form_template_id: { _eq: $form_template_id }
          deleted_at: { _is_null: true }
        }
      }
    ) {
      id
      name
    }
  }
`;

export const GET_RANKS_FOR_FILTERS = gql`
  query GetRanksForFilters($form_template_id: uuid!) {
    form_school_rank(
      where: { form: { form_template_id: { _eq: $form_template_id } } }
      order_by: { rank: asc }
      distinct_on: [rank]
    ) {
      rank
    }
  }
`;

export const GET_TAGS_FOR_FILTERS = gql`
  query GetTagsForFilters($enrollment_period_id: uuid!) {
    enrollment_period_tag(
      where: { enrollment_period_id: { _eq: $enrollment_period_id } }
      order_by: [{ tag_group: { name: asc_nulls_first } }, { name: asc }]
    ) {
      id
      name
      tag_group {
        name
      }
    }
  }
`;

export const GET_PROGRAMS_FOR_CAPACITIES_FILTERS = gql`
  query GetProgramsForCapacitiesFilter($organization_id: uuid!) {
    program_group(where: { organization_id: { _eq: $organization_id } }) {
      programs(order_by: { order: asc }) {
        id
        label
      }
    }
  }
`;

export const GET_VERIFICATIONS_FOR_FILTERS = gql`
  query GetVerificationsForFilters($form_template_id: uuid!) {
    form_verification(
      where: {
        _and: { questions: {}, form_template_id: { _eq: $form_template_id } }
      }
    ) {
      id
      label
    }
  }
`;

export const GET_FORM_HISTORY = gql`
  ${FORM_TEMPLATE_FRAGMENT}

  query GetFormHistory(
    $form_id: uuid!
    $organization_id: uuid!
    $offset: Int
    $limit: Int
  ) {
    audit_form_transaction(
      where: { form_id: { _eq: $form_id } }
      order_by: { action_tstamp_tx: desc }
      limit: $limit
      offset: $offset
    ) {
      transaction_id
      action_tstamp_tx
      logged_actions(
        where: {
          _or: [
            {
              table_name: { _in: ["application", "form"] }
              row_id: { _eq: $form_id }
            }
            # Events in adjacent tables that directly reference the form.
            { form_id: { _eq: $form_id } }
            # Events that don't directly reference any specific forms.
            {
              table_name: { _nin: ["application", "form"] }
              form_id: { _is_null: true }
            }
          ]
        }
        order_by: { action_tstamp_clk: desc }
      ) {
        user_id
        user {
          people(
            where: {
              _or: [
                { organization_id: { _eq: $organization_id } }
                { person_type: { _eq: admin } }
              ]
            }
          ) {
            id
            full_name
            person_type
          }
        }
        action
        action_tstamp_clk
        table_name
        row_data
        changed_fields
      }
    }
    audit_form_transaction_aggregate(where: { form_id: { _eq: $form_id } }) {
      aggregate {
        count
      }
    }
    form_by_pk(id: $form_id) {
      form_template {
        ...FormTemplateFragment
      }
    }
    school(where: { organization_id: { _eq: $organization_id } }) {
      id
      name
    }
    grade(
      where: {
        enrollment_period: { organization_id: { _eq: $organization_id } }
      }
      order_by: [{ grade_config: { order: asc } }]
    ) {
      id
      grade_config {
        id
        label
      }
      program {
        id
        label
      }
    }
    enrollment_period_tag(
      where: {
        enrollment_period: { organization_id: { _eq: $organization_id } }
      }
    ) {
      id
      name
    }
    document_metadata(where: { form_answer: { form_id: { _eq: $form_id } } }) {
      document_id
      filename
      created_at
      updated_at
      deleted_at
      document_type
      form_answer {
        id
        form_question {
          question {
            question
          }
        }
      }
      owner_user_id
      owner_user {
        id
        people(
          where: {
            _or: [
              { organization_id: { _eq: $organization_id } }
              { person_type: { _eq: admin } }
            ]
          }
        ) {
          full_name
          person_type
        }
      }
    }
  }
`;

export const FETCH_ACTION_ITEMS = gql`
  ${ACTION_ITEM_FRAGMENT}

  query FetchActionItems($form_id: uuid!, $status: action_item_status_enum!) {
    action_item(
      where: { status: { _eq: $status }, form_id: { _eq: $form_id } }
    ) {
      ...ActionItemFragment
    }
  }
`;

export const FETCH_FORM_STATUS = gql`
  query FetchFormStatus($form_ids: [uuid!]) {
    form(where: { id: { _in: $form_ids } }, distinct_on: status) {
      status
    }
  }
`;

export const FETCH_FORM_RECIPIENTS_MAX_INPUT_SIZE = 500;
export const FETCH_FORM_RECIPIENTS = gql`
  query FetchFormRecipients($form_ids: [uuid!]!, $organization_id: uuid!) {
    form(where: { id: { _in: $form_ids } }) {
      id
      applicant: person {
        id
        first_name
        last_name
      }
      applicant_guardians(
        where: {
          guardian: {
            organization_id: { _eq: $organization_id }
            active: { _eq: true }
            deleted_at: { _is_null: true }
          }
        }
      ) {
        guardian {
          id
          first_name
          last_name
          person_type
          phone_number
          sms_okay
          email_address
          email_okay
          preferred_language
        }
      }
    }
  }
`;

export const FETCH_OFFER_WAITLIST_STATUS_MAX_INPUT_SIZE = 500;
export const FETCH_OFFER_WAITLIST_STATUS = gql`
  query FetchOfferWaitlistStatus($search_keys: [form_school_rank_bool_exp!]!) {
    form_school_rank(where: { _or: $search_keys }) {
      id
      form_id
      school_id
      sub_status
      form {
        status
      }
      offers(where: { deleted_at: { _is_null: true } }) {
        id
        status
        expires_at
        assigned_forms(where: { deleted_at: { _is_null: true } }) {
          id
        }
      }
      waitlists(where: { deleted_at: { _is_null: true } }) {
        id
        status
        assigned_forms(where: { deleted_at: { _is_null: true } }) {
          id
        }
      }
    }
  }
`;

export const FETCH_GRADE_IDS_WITH_FORM_SCHOOL_MAX_INPUT_SIZE = 500;
export const FETCH_GRADE_IDS_WITH_FORM_SCHOOL = gql`
  query FetchGradeIdsWithFormSchool($query: form_school_grade_bool_exp!) {
    form_school_grade(where: $query) {
      form_id
      school_id
      grade_id
      form_school_rank_id
    }
  }
`;

export const FETCH_SEATS_AVAILABLE = gql`
  query FetchSeatsAvailable($grade_ids: [uuid!]) {
    grade(where: { id: { _in: $grade_ids } }) {
      id
      school {
        name
      }
      grade_config {
        label
      }
      bin {
        seats_available
      }
    }
  }
`;

export const GET_PROGRAM_GROUPS_BY_ORG = gql`
  query GetProgramGroupsByOrg($organization_id: uuid) {
    program_group(where: { organization_id: { _eq: $organization_id } }) {
      id
      name
      organization_id
      programs {
        id
        label
        order
      }
    }
  }
`;

export const GET_VERIFICATIONS_FOR_ADMIN = gql`
  query GetVerificationsForAdmin($form_id: uuid!) {
    form_verification(
      where: {
        _and: {
          questions: {}
          form_template: { forms: { id: { _in: [$form_id] } } }
        }
      }
    ) {
      id
      label
      questions {
        id
      }
    }
    form_verification_result(where: { form_id: { _eq: $form_id } }) {
      id
      form_verification_id
      verification_status
    }
  }
`;

export const GET_APPLICABLE_VERIFICATIONS_SUMMARIES = gql`
  query GetApplicableVerificationsSummaries(
    $form_template_id: uuid!
    $form_ids: [uuid!]!
  ) {
    form_verification(where: { form_template_id: { _eq: $form_template_id } }) {
      id
      label
      questions {
        form_template_section_id
        ancestor_questions {
          form_question_option {
            form_answer_options(
              distinct_on: form_answer_id
              where: { form_answer: { form_id: { _in: $form_ids } } }
            ) {
              form_answer {
                form_id
              }
            }
          }
        }
      }
      form_verification_results(where: { form_id: { _in: $form_ids } }) {
        form_id
        verification_status
      }
    }
  }
`;

export const GET_FORM_MESSAGES = gql`
  query GetFormMessages(
    $form_id: uuid!
    $message_receiver_filter: message_adhoc_form_receiver_bool_exp!
    $message_form_status_filter: message_form_status_bool_exp!
    $message_offer_status_filter: offer_bool_exp!
    $message_waitlist_status_filter: waitlist_bool_exp!
    $skip_content: Boolean!
  ) {
    message_adhoc_form_receiver(
      where: {
        _and: [{ form_id: { _eq: $form_id } }, $message_receiver_filter]
      }
    ) {
      message_id
      message_type
      sent_at
      message_adhoc {
        sender_person {
          full_name
          user {
            name
          }
        }
        message_adhoc_payload_email {
          message_body
          message_subject
        }
        message_adhoc_payload_sms {
          message_body
        }
        message_adhoc_payload_rich_contents {
          content_id
        }
      }
      form_id
      applicant_id
      receiver_person {
        phone_number
        email_address
      }
      receiver_contact_information
      message_status
    }
    message_form_status(
      where: {
        _and: [{ form_id: { _eq: $form_id } }, $message_form_status_filter]
      }
    ) {
      id
      message_type
      form_transition
      created_at
      form {
        form_template {
          enrollment_period {
            organization {
              id
              name
            }
          }
        }
      }
      receiver_contact_information
      message_subject @skip(if: $skip_content)
      message_body @skip(if: $skip_content)
      message_status
    }
    message_offer_status(
      where: {
        offer: {
          _and: [{ form_id: { _eq: $form_id } }, $message_offer_status_filter]
        }
      }
    ) {
      id
      message_type
      offer_transition
      created_at
      offer {
        form {
          form_template {
            enrollment_period {
              organization {
                id
                name
              }
            }
          }
        }
      }
      receiver_contact_information
      message_subject @skip(if: $skip_content)
      message_body @skip(if: $skip_content)
      message_status
    }
    message_waitlist_status(
      where: {
        waitlist: {
          _and: [
            { form_id: { _eq: $form_id } }
            $message_waitlist_status_filter
          ]
        }
      }
    ) {
      id
      message_type
      waitlist_transition
      created_at
      waitlist {
        form {
          form_template {
            enrollment_period {
              organization {
                id
                name
              }
            }
          }
        }
      }
      receiver_contact_information
      message_subject @skip(if: $skip_content)
      message_body @skip(if: $skip_content)
      message_status
    }
  }
`;

export const GET_ASSIGNED_FORMS_BY_FORM_ID = gql`
  query GetAssignedFormsByFormId($form_id: uuid!) {
    assigned_form(
      where: {
        has_started: { _eq: false }
        deleted_at: { _is_null: true }
        previous_form_id: { _eq: $form_id }
      }
    ) {
      id
      form_template {
        id
        name
      }
      person_id
      person {
        first_name
      }
      previous_form_id
      previous_offer_id
      previous_waitlist_id
    }
  }
`;
